import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { MCP_OAUTH_SCOPES, toggleMcpScope } from '@/components/McpOAuthCard';

interface Consent {
  clientId: string;
  name: string;
  publisherHostname?: string;
  scopes: string[];
  csrfToken: { parameterName: string; token: string };
}

export function McpOAuthConsent() {
  const { user, impersonation } = useAuth();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const clientId = params.get('client_id') ?? '';
  const state = params.get('state') ?? '';
  const scopeParameter = params.get('scope') ?? '';
  const valid = Boolean(clientId && state && scopeParameter)
    && ['client_id', 'state', 'scope'].every(key => params.getAll(key).length === 1);
  const [consent, setConsent] = useState<Consent | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setConsent(null); setSelected([]); setError(null); setBusy(false);
    if (!valid || impersonation) return;
    const requested = new Set(scopeParameter.split(/\s+/).filter(Boolean));
    api.get<Consent>(`/api/auth/mcp-oauth/consent?client_id=${encodeURIComponent(clientId)}`)
      .then(data => {
        if (!active) return;
        if (data.clientId !== clientId || !data.csrfToken?.token || !data.csrfToken.parameterName) {
          throw new Error('Nie udało się zweryfikować prośby o dostęp. Rozpocznij połączenie ponownie w Claude.');
        }
        const allowed = new Set([...data.scopes, 'offline_access']);
        if ([...requested].some(scope => !allowed.has(scope))) {
          throw new Error('Żądany zakres przekracza uprawnienia tego połączenia. Utwórz nowe połączenie z właściwym zakresem.');
        }
        if (MCP_OAUTH_SCOPES.some(scope => 'requires' in scope && requested.has(scope.code) && !requested.has(scope.requires))) {
          throw new Error('Prośba o zapis musi obejmować również odczyt odpowiedniego rejestru. Rozpocznij połączenie ponownie z pełnym zakresem.');
        }
        setConsent(data);
        setSelected([...requested]);
      })
      .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Nie udało się pobrać prośby o dostęp.'); });
    return () => { active = false; };
  }, [clientId, scopeParameter, valid, impersonation, user?.id]);

  const requested = new Set(scopeParameter.split(/\s+/));
  const hiddenFields = consent && <>
    <input type="hidden" name="client_id" value={consent.clientId} />
    <input type="hidden" name="state" value={state} />
    <input type="hidden" name={consent.csrfToken.parameterName} value={consent.csrfToken.token} />
  </>;

  return <main className="min-h-screen bg-ink-50 grid place-items-center p-5">
    <section className="card w-full max-w-xl" aria-labelledby="oauth-consent-heading">
      <div className="card-head"><h1 className="card-title" id="oauth-consent-heading">Połącz Claude z portalem</h1></div>
      <div className="card-body space-y-4 text-[14px]">
        {!valid ? <p role="alert">Nieprawidłowy lub niekompletny link. Wróć do Claude i ponownie kliknij Connect.</p>
          : impersonation ? <p role="alert">Wróć na własne konto przed zatwierdzeniem połączenia OAuth.</p>
          : error ? <p role="alert" className="text-red-600">{error}</p>
          : !consent ? <p>Sprawdzanie prośby o dostęp…</p>
          : <>
            <p>Połączenie <strong>{consent.name}</strong> uzyska dostęp do portalu jako <strong>{user?.login}</strong>.</p>
            {consent.publisherHostname && <p>Wydawca aplikacji: <strong>{consent.publisherHostname}</strong>.</p>}
            <p>Wybierz czynności, które Claude może wykonywać. Uprawnienia połączenia są ograniczone Twoimi aktualnymi uprawnieniami w portalu.</p>
            <form method="post" action="/api/oauth2/authorize" onSubmit={() => setBusy(true)} className="space-y-4">
              {hiddenFields}
              {selected.map(scope => <input key={scope} type="hidden" name="scope" value={scope} />)}
              <fieldset className="space-y-3"><legend className="font-semibold mb-3">Zakres dostępu</legend>
                {MCP_OAUTH_SCOPES.filter(scope => requested.has(scope.code)).map(scope => <label key={scope.code} className="flex items-center gap-2">
                  <input type="checkbox" checked={selected.includes(scope.code)} disabled={busy}
                    onChange={event => setSelected(current => toggleMcpScope(current, scope.code, event.target.checked))} />
                  {scope.label}
                </label>)}
                {requested.has('offline_access') && <label className="flex items-start gap-2">
                  <input type="checkbox" checked={selected.includes('offline_access')} disabled={busy}
                    onChange={event => setSelected(current => event.target.checked
                      ? [...new Set([...current, 'offline_access'])] : current.filter(scope => scope !== 'offline_access'))} />
                  <span>Utrzymanie połączenia do 90 dni bez ponownego logowania.</span>
                </label>}
              </fieldset>
              <p className="text-ink-500 text-[13px]">Dostęp możesz unieważnić w Ustawieniach konta → Claude - połączenie OAuth.</p>
              <button className="btn btn-primary" disabled={busy || !selected.some(scope => scope !== 'offline_access')}>
                {busy ? 'Przekierowywanie…' : 'Zezwól na wybrany dostęp'}</button>
            </form>
            <form method="post" action="/api/oauth2/authorize" onSubmit={() => setBusy(true)}>
              {hiddenFields}
              <button className="btn btn-secondary" disabled={busy}>Odmów dostępu</button>
            </form>
          </>}
      </div>
    </section>
  </main>;
}
